import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, X } from "lucide-react";
import Lottie from "lottie-react";
import octopusAnimation from "../assets/Octopus.json";
import loadingAnimation from "../assets/Loading.json"; // Added Loading.json import

const AccountPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false); // Added state for navigation
  const [loginData, setLoginData] = useState({ whatsapp: '', password: '' });
  const [registerData, setRegisterData] = useState({
    childImage: null,
    fullName: '',
    password: '',
    dob: null,
    gender: '',
    state: '',
    country: 'India',
    schoolCode: '',
    whatsapp: '',
    plan: 'Silver'
  });
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const [ageWarning, setAgeWarning] = useState('');
  const [isRegistered, setIsRegistered] = useState(false);
  const [step, setStep] = useState(1);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const plans = [
    { name: "Silver", price: "200 Rs", features: ["Core Activities", "Daily Challenges", "Progress Tracking", "Free Scholarship Test"], bgColor: "#C0C0C0", accentColor: "#808080" },
    { name: "Gold", price: "300 Rs", features: ["All Basic Features", "IIT Prep Pack", "Weekly Reports", "Free Scholarship Test"], bgColor: "#FFD700", accentColor: "#FFA500" },
    { name: "Diamond", price: "600 Rs", features: ["All Plus Features", "1:1 AI Mentor", "Priority Support", "Free Scholarship Test"], bgColor: "#00CED1", accentColor: "#1E90FF" }
  ];

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const indianStates = [
    'Andaman and Nicobar Islands', 'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar',
    'Chandigarh', 'Chhattisgarh', 'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Goa',
    'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jammu and Kashmir', 'Jharkhand', 'Karnataka',
    'Kerala', 'Ladakh', 'Lakshadweep', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
    'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Puducherry', 'Punjab', 'Rajasthan',
    'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
  ];

  const [dateParts, setDateParts] = useState({
    day: '',
    month: '',
    year: ''
  });

  const calculateAge = (dob) => {
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const validateStep = (currentStep) => {
    const newErrors = {};
    switch (currentStep) {
      case 1:
        if (!registerData.childImage) newErrors.childImage = "Please upload a child's image";
        break;
      case 2:
        if (!registerData.fullName.trim()) newErrors.fullName = "Full name is required";
        if (!/^\d{10,12}$/.test(registerData.whatsapp)) newErrors.whatsapp = "Enter a valid WhatsApp number (10-12 digits)";
        break;
      case 3:
        if (!registerData.password) newErrors.password = "Password is required";
        if (!dateParts.day || !dateParts.month || !dateParts.year) {
          newErrors.dob = "Complete date of birth is required";
        } else {
          const monthIndex = months.indexOf(dateParts.month);
          const date = new Date(dateParts.year, monthIndex, dateParts.day);
          if (isNaN(date.getTime())) {
            newErrors.dob = "Invalid date";
          } else {
            const age = calculateAge(date);
            if (age > 15) newErrors.dob = "Only kids 15 and under can register";
          }
        }
        break;
      case 4:
        if (!registerData.gender) newErrors.gender = "Please select a gender";
        if (!registerData.state.trim()) newErrors.state = "Please select a state";
        break;
      case 5:
        if (!registerData.plan) newErrors.plan = "Please select a plan";
        break;
      default:
        break;
    }
    console.log('Validation errors:', newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!loginData.whatsapp) newErrors.whatsapp = "WhatsApp number is required";
    if (!loginData.password) newErrors.password = "Password is required";

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData),
      });

      console.log('Login response status:', response.status, 'Content-Type:', response.headers.get('Content-Type'));

      if (!response.ok) {
        const contentType = response.headers.get('Content-Type') || '';
        let errorMessage = 'Login failed';
        if (contentType.includes('application/json')) {
          const data = await response.json();
          errorMessage = data.message || errorMessage;
        } else {
          const text = await response.text();
          console.error('Non-JSON response:', text);
          errorMessage = 'Unexpected server response';
        }
        setErrors({ general: errorMessage });
        return;
      }

      const data = await response.json();
      localStorage.setItem('token', data.token);
      setIsNavigating(true); // Trigger loading animation
      navigate('/play');
    } catch (err) {
      console.error('Login error:', err);
      setErrors({ general: 'Network error. Please check your connection and try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (isLoading || !validateStep(5)) return;

    setIsLoading(true);
    try {
      const minDelay = new Promise(resolve => setTimeout(resolve, 2000));
      const reader = new FileReader();
      const apiPromise = new Promise((resolve, reject) => {
        reader.readAsDataURL(registerData.childImage);
        reader.onloadend = async () => {
          try {
            const base64Image = reader.result;
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/register`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                ...registerData,
                childImage: base64Image,
              }),
            });

            console.log('Register response status:', response.status, 'Content-Type:', response.headers.get('Content-Type'));

            if (!response.ok) {
              const contentType = response.headers.get('Content-Type') || '';
              let errorMessage = 'Registration failed';
              if (contentType.includes('application/json')) {
                const data = await response.json();
                errorMessage = data.message || errorMessage;
              } else {
                const text = await response.text();
                console.error('Non-JSON response:', text);
                errorMessage = 'Unexpected server response';
              }
              throw new Error(errorMessage);
            }

            const data = await response.json();
            localStorage.setItem('token', data.token);
            setIsRegistered(true);
            setIsNavigating(true); // Trigger loading animation
            navigate('/play'); // Navigate to /play after registration
            resolve();
          } catch (err) {
            reject(err);
          }
        };
        reader.onerror = () => reject(new Error('Failed to read image file'));
      });

      await Promise.all([minDelay, apiPromise]);
    } catch (err) {
      setErrors({ general: err.message || 'Registration failed. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setErrors({ ...errors, childImage: 'Please upload a valid image file' });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setErrors({ ...errors, childImage: 'Image size must be less than 5MB' });
        return;
      }
      setRegisterData({ ...registerData, childImage: file });
      setImagePreview(URL.createObjectURL(file));
      setErrors({ ...errors, childImage: '' });
    }
  };

  const handleImageClick = () => fileInputRef.current?.click();
  const handleRemoveImage = () => {
    setImagePreview(null);
    setRegisterData({ ...registerData, childImage: null });
    fileInputRef.current.value = null;
    setErrors({ ...errors, childImage: "Please upload a child's image" });
  };

  const handlePlanSelect = (planName) => {
    setRegisterData({ ...registerData, plan: planName });
    setErrors({ ...errors, plan: '' });
  };

  const handleDateChange = (field) => (e) => {
    const value = e.target.value;
    const newDateParts = { ...dateParts, [field]: value };

    if (field === 'day' && (value > 31 || value < 1)) return;
    if (field === 'year' && value > new Date().getFullYear()) return;

    setDateParts(newDateParts);
    setErrors({ ...errors, dob: '' });

    if (newDateParts.day && newDateParts.month && newDateParts.year) {
      const monthIndex = months.indexOf(newDateParts.month);
      const date = new Date(newDateParts.year, monthIndex, dateParts.day);
      if (!isNaN(date.getTime())) {
        setRegisterData({ ...registerData, dob: date });
        const age = calculateAge(date);
        setAgeWarning(`Age: ${age} years`);
        if (age > 15) {
          setErrors({ ...errors, dob: "Only kids 15 and under can register" });
        }
      }
    }
  };

  const handleGenderSelect = (gender) => {
    console.log('Selected gender:', gender);
    setRegisterData({ ...registerData, gender });
    setErrors({ ...errors, gender: '' });
  };

  useEffect(() => {
    console.log('Current gender:', registerData.gender);
  }, [registerData.gender]);

  const nextStep = () => {
    if (validateStep(step)) {
      setStep((prev) => prev + 1);
      setErrors({});
    }
  };

  const prevStep = () => {
    setStep((prev) => prev - 1);
    setErrors({});
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-neutral mb-1">
                Child's Image
              </label>
              <div
                className="relative w-full aspect-square bg-white border-2 border-dashed border-primary/20 rounded-xl flex items-center justify-center cursor-pointer hover:border-primary transition-all duration-300 group"
                onClick={handleImageClick}
              >
                {imagePreview ? (
                  <>
                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover rounded-xl" />
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleRemoveImage(); }}
                      className="absolute top-2 right-2 bg-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-primary/10"
                    >
                      <X className="h-4 w-4 text-primary" />
                    </button>
                  </>
                ) : (
                  <div className="text-center text-primary p-4">
                    <Upload className="mx-auto h-8 w-8 mb-2 group-hover:scale-110 transition-transform" />
                    <span className="text-sm font-medium">Click to upload image</span>
                    <p className="text-xs text-neutral mt-1">Max 5MB</p>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  className="hidden"
                  onChange={handleImageUpload}
                />
              </div>
              {errors.childImage && (
                <p className="text-sm text-red-500 mt-1">{errors.childImage}</p>
              )}
            </div>
            <button
              onClick={nextStep}
              className="w-full border-2 border-primary text-primary font-semibold py-2.5 rounded-full transition-all duration-300 hover:border-primary hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed hover-effect"
            >
              Next
            </button>
          </div>
        );
      case 2:
        return (
          <div className="space-y-6">
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-neutral mb-1">
                Full Name
              </label>
              <input
                id="fullName"
                type="text"
                value={registerData.fullName}
                onChange={(e) => {
                  setRegisterData({ ...registerData, fullName: e.target.value });
                  setErrors({ ...errors, fullName: '' });
                }}
                className="w-full px-4 py-2 rounded-full border border-primary/20 bg-white text-dark placeholder-neutral focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 hover:bg-primary/5"
                placeholder="Enter child's full name"
              />
              {errors.fullName && (
                <p className="text-sm text-red-500 mt-1">{errors.fullName}</p>
              )}
            </div>
            <div>
              <label htmlFor="whatsapp" className="block text-sm font-medium text-neutral mb-1">
                WhatsApp Number
              </label>
              <input
                id="whatsapp"
                type="tel"
                value={registerData.whatsapp}
                onChange={(e) => {
                  setRegisterData({ ...registerData, whatsapp: e.target.value });
                  setErrors({ ...errors, whatsapp: '' });
                }}
                className="w-full px-4 py-2 rounded-full border border-primary/20 bg-white text-dark placeholder-neutral focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 hover:bg-primary/5"
                placeholder="Enter WhatsApp number"
              />
              {errors.whatsapp && (
                <p className="text-sm text-red-500 mt-1">{errors.whatsapp}</p>
              )}
            </div>
            <div className="flex gap-4">
              <button
                onClick={prevStep}
                className="flex-1 bg-white text-neutral py-2.5 rounded-full border border-primary/20 hover:bg-primary/10 transition-all duration-200"
              >
                Back
              </button>
              <button
                onClick={nextStep}
                className="flex-1 border-2 border-primary text-primary font-semibold py-2.5 rounded-full transition-all duration-300 hover:border-primary hover:text-primary hover-effect"
              >
                Next
              </button>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-6">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-neutral mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={registerData.password}
                onChange={(e) => {
                  setRegisterData({ ...registerData, password: e.target.value });
                  setErrors({ ...errors, password: '' });
                }}
                className="w-full px-4 py-2 rounded-full border border-primary/20 bg-white text-dark placeholder-neutral focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 hover:bg-primary/5"
                placeholder="Enter a password"
              />
              {errors.password && (
                <p className="text-sm text-red-500 mt-1">{errors.password}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral mb-1">
                Date of Birth
              </label>
              <div className="grid grid-cols-3 gap-2">
                <input
                  type="number"
                  value={dateParts.day}
                  onChange={handleDateChange('day')}
                  placeholder="Day"
                  min="1"
                  max="31"
                  className="w-full px-3 py-2 rounded-full border border-primary/20 bg-white text-dark placeholder-neutral focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 hover:bg-primary/5"
                />
                <select
                  value={dateParts.month}
                  onChange={handleDateChange('month')}
                  className="w-full px-3 py-2 rounded-full border border-primary/20 bg-white text-dark placeholder-neutral focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 hover:bg-primary/5 appearance-none"
                >
                  <option value="" className="text-dark">Month</option>
                  {months.map((month) => (
                    <option key={month} value={month} className="text-dark">{month}</option>
                  ))}
                </select>
                <input
                  type="number"
                  value={dateParts.year}
                  onChange={handleDateChange('year')}
                  placeholder="Year"
                  min="1900"
                  max={new Date().getFullYear()}
                  className="w-full px-3 py-2 rounded-full border border-primary/20 bg-white text-dark placeholder-neutral focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 hover:bg-primary/5"
                />
              </div>
              {ageWarning && !errors.dob && (
                <p className="text-sm text-primary mt-2 animate-fade-in">
                  {ageWarning}
                </p>
              )}
              {errors.dob && (
                <p className="text-sm text-red-500 mt-1">{errors.dob}</p>
              )}
            </div>
            <div className="flex gap-4">
              <button
                onClick={prevStep}
                className="flex-1 bg-white text-neutral py-2.5 rounded-full border border-primary/20 hover:bg-primary/10 transition-all duration-200"
              >
                Back
              </button>
              <button
                onClick={nextStep}
                className="flex-1 border-2 border-primary text-primary font-semibold py-2.5 rounded-full transition-all duration-300 hover:border-primary hover:text-primary hover-effect"
              >
                Next
              </button>
            </div>
          </div>
        );
      case 4:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-neutral mb-1">
                Gender
              </label>
              <div className="flex gap-4">
                {['male', 'female'].map((gender) => (
                  <button
                    key={gender}
                    type="button"
                    onClick={() => handleGenderSelect(gender)}
                    aria-pressed={registerData.gender === gender}
                    className={`flex-1 py-2 px-4 rounded-full border transition-all duration-200 ${
                      registerData.gender === gender
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    {gender.charAt(0).toUpperCase() + gender.slice(1)}
                  </button>
                ))}
              </div>
              {errors.gender && (
                <p className="text-sm text-red-500 mt-1">{errors.gender}</p>
              )}
            </div>
            <div>
              <label htmlFor="state" className="block text-sm font-medium text-neutral mb-1">
                State
              </label>
              <select
                id="state"
                value={registerData.state}
                onChange={(e) => {
                  setRegisterData({ ...registerData, state: e.target.value });
                  setErrors({ ...errors, state: '' });
                }}
                className="w-full px-4 py-2 rounded-full border border-primary/20 bg-white text-dark placeholder-neutral focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 hover:bg-primary/5 appearance-none"
              >
                <option value="" className="text-dark">Select a state</option>
                {indianStates.map((state) => (
                  <option key={state} value={state} className="text-dark">{state}</option>
                ))}
              </select>
              {errors.state && (
                <p className="text-sm text-red-500 mt-1">{errors.state}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral mb-1">
                Country
              </label>
              <div className="w-full px-4 py-2 rounded-full border border-primary/20 bg-gray-100 text-dark">
                India
              </div>
            </div>
            <div className="flex gap-4">
              <button
                onClick={prevStep}
                className="flex-1 bg-white text-neutral py-2.5 rounded-full border border-primary/20 hover:bg-primary/10 transition-all duration-200"
              >
                Back
              </button>
              <button
                onClick={nextStep}
                className="flex-1 border-2 border-primary text-primary font-semibold py-2.5 rounded-full transition-all duration-300 hover:border-primary hover:text-primary hover-effect"
              >
                Next
              </button>
            </div>
          </div>
        );
      case 5:
        return (
          <div className="space-y-6">
            <div>
              <label htmlFor="schoolCode" className="block text-sm font-medium text-neutral mb-1">
                School Code (Optional)
              </label>
              <input
                id="schoolCode"
                type="text"
                value={registerData.schoolCode}
                onChange={(e) => {
                  setRegisterData({ ...registerData, schoolCode: e.target.value });
                  setErrors({ ...errors, schoolCode: '' });
                }}
                className="w-full px-4 py-2 rounded-full border border-primary/20 bg-white text-dark placeholder-neutral focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 hover:bg-primary/5"
                placeholder="Enter school code"
              />
              {errors.schoolCode && (
                <p className="text-sm text-red-500 mt-1">{errors.schoolCode}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral mb-2">
                Choose Your Plan
              </label>
              <div className="grid grid-cols-1 gap-4">
                {plans.map((plan) => (
                  <div
                    key={plan.name}
                    className={`pricing-plan cursor-pointer ${
                      registerData.plan === plan.name
                        ? 'border-2 border-blue-600 scale-105 shadow-md'
                        : 'border border-primary/20 hover:border-primary/50'
                    }`}
                    onClick={() => handlePlanSelect(plan.name)}
                  >
                    <div className="pricing-header" style={{ backgroundColor: plan.bgColor }}>
                      <h3>{plan.name}</h3>
                    </div>
                    <div className="pricing-body">
                      <p className="price">{plan.price}<span>/month</span></p>
                      <ul>
                        {plan.features.map((feature, i) => (
                          <li key={i}>{feature}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
              {errors.plan && (
                <p className="text-sm text-red-500 mt-1">{errors.plan}</p>
              )}
            </div>
            <div className="flex gap-4">
              <button
                onClick={prevStep}
                className="flex-1 bg-white text-neutral py-2.5 rounded-full border border-primary/20 hover:bg-primary/10 transition-all duration-200"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 border-2 border-primary text-primary font-semibold py-2.5 rounded-full transition-all duration-300 hover:border-primary hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed hover-effect"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Registering...
                  </span>
                ) : (
                  'Register'
                )}
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (isNavigating) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[var(--light)]"
        aria-label="Navigating to profile"
      >
        <Lottie
          animationData={loadingAnimation}
          loop={true}
          style={{ width: "200px", height: "200px" }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="fixed-header">
        <Lottie
          animationData={octopusAnimation}
          loop={true}
          style={{ width: "200px", height: "100px" }}
        />
      </div>
      <div className="container flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 agent-card p-6 sm:p-8">
          {isRegistered ? (
            <div className="text-center space-y-6 animate-fade-in">
              <h2 className="text-2xl font-bold text-dark">
                Thank You!
              </h2>
              <p className="text-neutral">
                Thanks for your interest! We are receiving a huge demand from parents—our team will message you soon!
              </p>
              <button
                onClick={() => navigate('/')}
                className="border-2 border-primary text-primary font-semibold py-2.5 px-6 rounded-full transition-all duration-300 hover:border-primary hover:text-primary hover-effect"
              >
                Back to Home
              </button>
            </div>
          ) : (
            <>
              <div className="flex justify-center gap-4 mb-6 border-b border-primary/20">
                {['Login', 'Register'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setIsLogin(tab === 'Login')}
                    className={`px-4 py-2 font-medium text-sm transition-all duration-200 relative ${
                      (isLogin && tab === 'Login') || (!isLogin && tab === 'Register')
                        ? 'text-blue-600 after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-blue-600'
                        : 'text-neutral hover:text-primary'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {errors.general && (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-lg animate-fade-in">
                  {errors.general}
                </div>
              )}

              <div className="transition-all duration-300">
                {isLogin ? (
                  <form onSubmit={handleLoginSubmit} className="space-y-6">
                    {[
                      { id: 'whatsapp', label: 'WhatsApp Number', type: 'tel', placeholder: 'Enter your WhatsApp number' },
                      { id: 'password', label: 'Password', type: 'password', placeholder: 'Enter your password' },
                    ].map(({ id, label, type, placeholder }) => (
                      <div key={id}>
                        <label htmlFor={id} className="block text-sm font-medium text-neutral mb-1">
                          {label}
                        </label>
                        <input
                          id={id}
                          type={type}
                          value={loginData[id]}
                          onChange={(e) => {
                            setLoginData({ ...loginData, [id]: e.target.value });
                            setErrors({ ...errors, [id]: '' });
                          }}
                          className="w-full px-4 py-2 rounded-full border border-primary/20 bg-white text-dark placeholder-neutral focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 hover:bg-primary/5"
                          placeholder={placeholder}
                        />
                        {errors[id] && (
                          <p className="text-sm text-red-500 mt-1">{errors[id]}</p>
                        )}
                      </div>
                    ))}
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full border-2 border-primary text-primary font-semibold py-2.5 rounded-full transition-all duration-300 hover:border-primary hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed hover-effect"
                    >
                      {isLoading ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          Logging in...
                        </span>
                      ) : (
                        'Login'
                      )}
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handleRegisterSubmit} className="space-y-6">
                    {renderStep()}
                  </form>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <style jsx>{`
        :root {
          --primary: #1E90FF;
          --dark: #1a1a1a;
          --light: #ffffff;
          --neutral: #4a5568;
          --dark-blue: #4682B4;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .min-h-screen {
          background-color: var(--light);
          color: var(--dark);
          position: relative;
          overflow-x: hidden;
          font-family: 'Inter', sans-serif;
          line-height: 1.6;
          -webkit-font-smoothing: antialiased;
        }

        .fixed-header {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 120px;
          background: var(--light);
          border-bottom: 1px solid rgba(30, 144, 255, 0.2);
          z-index: 10;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 0 16px;
        }

        .container {
          margin-top: 120px;
          min-height: calc(100vh - 120px);
          overflow-y: auto;
        }

        .agent-card {
          position: relative;
          background: rgba(255, 255, 255, 0.95);
          border-radius: 1rem;
          border: 1px solid rgba(30, 144, 255, 0.2);
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
          transition: all 0.3s ease;
          overflow: hidden;
          word-wrap: break-word;
        }

        .agent-card:hover {
          box-shadow: 0 4px 15px rgba(30, 144, 255, 0.2);
        }

        h2 {
          font-size: clamp(1.25rem, 5vw, 2rem);
          font-weight: 700;
          color: var(--dark);
        }

        p {
          color: var(--neutral);
          font-size: clamp(0.875rem, 4vw, 1rem);
        }

        .pricing-plan {
          display: flex;
          flex-direction: column;
          background: rgba(240, 240, 240, 0.8);
          border-radius: 0.75rem;
          overflow: hidden;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
          transition: transform 0.2s ease;
        }

        .pricing-plan:hover {
          transform: translateY(-3px);
        }

        .pricing-header {
          padding: 1rem;
          text-align: center;
          color: var(--dark);
          font-weight: 700;
          font-size: clamp(1rem, 4vw, 1.25rem);
        }

        .pricing-body {
          padding: 1rem;
          flex-grow: 1;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .pricing-plan ul {
          list-style: none;
          margin: 1rem 0;
          text-align: left;
        }

        .pricing-plan li {
          color: var(--neutral);
          font-size: clamp(0.75rem, 3vw, 0.875rem);
          margin-bottom: 0.5rem;
          position: relative;
          padding-left: 1.25rem;
        }

        .pricing-plan li:before {
          content: '✓';
          position: absolute;
          left: 0;
          color: var(--primary);
          font-weight: bold;
        }

        .price {
          font-size: clamp(1.5rem, 6vw, 2rem);
          font-weight: 800;
          color: var(--dark);
          margin-bottom: 1rem;
          text-align: center;
        }

        .price span {
          font-size: clamp(0.75rem, 3vw, 1rem);
          color: var(--neutral);
          font-weight: 400;
        }

        .hover-effect:hover {
          border-color: var(--primary) !important;
          color: var(--primary) !important;
        }

        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }

        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        input[type="number"] {
          -moz-appearance: textfield;
        }

        select {
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%231E90FF' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E");
          background-position: right 0.5rem center;
          background-repeat: no-repeat;
          background-size: 1.5em 1.5em;
        }

        .error-text {
          color: #ef4444;
          font-size: 0.875rem;
          margin-top: 0.25rem;
          display: block;
        }

        @media (max-width: 768px) {
          .fixed-header {
            height: 100px;
          }

          .container {
            margin-top: 100px;
            min-height: calc(100vh - 100px);
          }

          .pricing-plan {
            padding: 0.75rem;
          }
        }

        @media (min-width: 640px) {
          .agent-card {
            padding: 2rem;
          }
        }

        @media (min-width: 1024px) {
          .agent-card {
            padding: 2.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default AccountPage;